import { cx } from '../cx';
import { Tooltip } from '../Tooltip/Tooltip';
import { type ChartFocus, ChartFrame } from './ChartFrame';
import {
  backgroundClass,
  type ChartPoint,
  formatValue,
  NO_DATA,
  type SeriesColor,
} from './chartModel';

/**
 * A FUNNEL IS LAID OUT, NOT DRAWN (`reports.md` T1).
 *
 * The picture the owner sent on 2026-09-02 makes the point: Attio's "Sales pipeline" panel
 * is a ROW OF STAGES with a count and a percentage each, not a tapering polygon. The
 * polygon is the worse of the two - its area encodes nothing, its slanted edges make
 * neighbouring stages hard to compare, and it has no honest answer for a stage that gains
 * records. So this is DOM: text a reader can select, a proportional bar, and a percentage.
 *
 * TWO PERCENTAGES ARE ONE TOO MANY, so the bar and the caption say different things: the
 * BAR is the stage's share of the first stage (the funnel's shape), and the CAPTION is the
 * conversion from the stage immediately before it (the number an operator acts on).
 *
 * Because the numbers are real text, the frame runs in `content` mode: marking this
 * `role="img"` would hide from a screen reader the very words a sighted reader is looking
 * at. AND FOR THE SAME REASON IT HAS NO HIDDEN DATA TABLE (T3, as amended by the owner on
 * 2026-09-06): the table exists so that numbers which are DRAWN can still be read, and
 * these are not drawn. Repeating them underneath would make a screen reader say the whole
 * funnel twice.
 */
export interface FunnelProps {
  readonly name: string;
  readonly titleHidden?: boolean | undefined;
  /** In funnel order, widest first. The component never re-sorts them. */
  readonly stages: readonly ChartPoint[];
  readonly loading?: boolean | undefined;
  readonly refetching?: boolean | undefined;
  readonly emptyMessage?: string | undefined;
  readonly format?: ((value: number) => string) | undefined;
  /** A click on a stage. Absent means the row is a picture, not a set of buttons. */
  readonly onSelect?: ((stage: ChartPoint) => void) | undefined;
}

export function Funnel({
  name,
  titleHidden,
  stages,
  loading = false,
  refetching = false,
  emptyMessage,
  format = formatValue,
  onSelect,
}: FunnelProps) {
  const empty = stages.length === 0;
  const first = stages[0]?.value ?? 0;

  const announce = ({ point }: ChartFocus): string => {
    const stage = stages[point];
    if (stage === undefined) return '';
    if (stage.value === null) return `${stage.label}: ${NO_DATA}`;
    const previous = stages[point - 1];
    const step =
      previous === undefined || previous.value === null || previous.value === 0
        ? ''
        : `, ${percent(stage.value, previous.value)} from ${previous.label}`;
    return `${stage.label}: ${format(stage.value)}${step}`;
  };

  return (
    <ChartFrame
      name={name}
      {...(titleHidden === undefined ? {} : { titleHidden })}
      height={120}
      sizing="min"
      loading={loading}
      refetching={refetching}
      {...(emptyMessage === undefined ? {} : { emptyMessage })}
      empty={empty}
      rows={1}
      columns={stages.length}
      announce={announce}
      presentation="content"
    >
      {({ focus }) => (
        <ol className="flex min-w-0 flex-col gap-2 md:flex-row md:items-stretch md:gap-3">
          {stages.map((stage, index) => {
            const previous = stages[index - 1];
            const focused = focus !== null && focus.point === index;
            const width =
              stage.value === null || first <= 0
                ? 0
                : Math.max(2, Math.min(100, (stage.value / first) * 100));
            const caption =
              index === 0
                ? 'Start of the funnel'
                : previous === undefined ||
                    previous.value === null ||
                    previous.value === 0 ||
                    stage.value === null
                  ? 'No comparison'
                  : `${percent(stage.value, previous.value)} from ${previous.label}`;

            return (
              <li
                key={stage.key}
                className={cx(
                  /*
                   * NO FRAME AROUND A STAGE (P4-1's rule, one screen further on). Each stage
                   * used to be a `rounded-control border border-line bg-surface p-3` box, which
                   * is a card by another name on a screen whose whole construction is panes on
                   * one ground. What separates the stages now is the gap and the bar, which is
                   * what separates them in the picture the owner sent.
                   */
                  'min-w-0 flex-1 rounded-control',
                  refetching ? '' : 'chart-fade-in',
                  focused && 'outline-2 outline-accent outline-offset-2',
                )}
              >
                <Tooltip label={`${stage.label}: ${valueText(stage, format)}. ${caption}.`}>
                  <div
                    {...(onSelect === undefined
                      ? {}
                      : { onClick: () => onSelect(stage), role: 'button', tabIndex: -1 })}
                    className="min-w-0"
                  >
                    <p className="truncate text-2xs font-medium text-ink-muted">{stage.label}</p>
                    <p className="text-lg font-semibold tracking-tight text-ink tabular-nums">
                      {stage.value === null ? (
                        <>
                          <span aria-hidden="true">&mdash;</span>
                          <span className="sr-only">{NO_DATA}</span>
                        </>
                      ) : (
                        format(stage.value)
                      )}
                    </p>
                    {/*
                        The bar is the funnel's SHAPE: every stage measured against the
                        first, so a reader sees the narrowing without doing arithmetic.
                      */}
                    <div
                      aria-hidden="true"
                      className="mt-2 h-1.5 w-full overflow-hidden rounded-pill bg-surface-sunken"
                    >
                      <div
                        className={cx('h-full rounded-pill', backgroundClass(stageColor(stage)))}
                        style={{ width: `${String(width)}%` }}
                      />
                    </div>
                    <p className="mt-1.5 truncate text-2xs text-ink-subtle tabular-nums">
                      {caption}
                    </p>
                  </div>
                </Tooltip>
              </li>
            );
          })}
        </ol>
      )}
    </ChartFrame>
  );
}

function valueText(stage: ChartPoint, format: (value: number) => string): string {
  return stage.value === null ? NO_DATA : format(stage.value);
}

/**
 * ONE COLOUR DOWN THE WHOLE FUNNEL, and this is not a shortcut.
 *
 * The stages are one population narrowing, not five categories side by side. Walking the
 * six-tone palette here - which is what a bar chart does, correctly - would paint five
 * different colours and say the stages are five different KINDS of thing, which is the
 * opposite of what a funnel means. So the accent, the one token DESIGN.md reserves for the
 * single highlighted thing, unless a stage carries a select option's own tone.
 */
function stageColor(stage: ChartPoint): SeriesColor {
  return stage.tone ?? 'highlight';
}

/** Whole percentages: a funnel read to one decimal place is precision nobody acts on. */
function percent(value: number, of: number): string {
  return `${String(Math.round((value / of) * 100))}%`;
}
